import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { MapPin, Calendar } from "lucide-react";
import { PlusMinus, EqualMark, DotGrid } from "@/components/site/Marks";

const Exhibition = () => {
  const { t } = useLang();
  const pillars = ["energia", "ekologia", "atom"];
  const dots = { energia: "#b02418", ekologia: "#1a5fa8", atom: "#f5f2eb" };

  return (
    <section id="exhibition" className="relative py-24 md:py-36 overflow-hidden">
      <div
        className="absolute inset-0 z-0 opacity-[0.10]"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/flagged/photo-1574885174114-bd4e8382d850?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#050505] via-[#050505]/85 to-[#050505]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <DotGrid className="absolute right-[2%] bottom-[6%] opacity-20 hidden lg:block" color="#f5f2eb" cols={6} rows={6} gap={12} />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="flex flex-col items-center text-center mb-20"
          data-testid="exhibition-poster"
        >
          <p className="overline text-[#d97925] mb-7">{t("exhibition.overline")}</p>
          <div className="relative">
            <div
              className="absolute -inset-8 -z-10"
              style={{ background: "radial-gradient(55% 55% at 50% 45%, rgba(217,121,37,0.20), rgba(5,5,5,0))" }}
            />
            <img
              src="https://customer-assets.emergentagent.com/job_artist-soul-journey/artifacts/1rudmfb0_PLAGAT%20HS%20A4%20S%20DATUMOM.jpeg"
              alt="Horizonty svetla a ticha — plagát výstavy"
              className="w-full max-w-[520px] h-auto shadow-[0_30px_80px_rgba(0,0,0,0.7)] spotlight"
            />
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <PlusMinus className="opacity-50 mb-5" color="#d97925" />
            <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              {t("exhibition.title")}
            </h2>
            <p className="mt-8 text-[#a3a09a] leading-relaxed font-light max-w-lg">{t("exhibition.body")}</p>

            <EqualMark className="mt-10 opacity-50" color="#d97925" w={40} />
            <div className="mt-6 border-l border-white/12 pl-6 space-y-7">
              <div className="flex items-start gap-4" data-testid="exhibition-location">
                <MapPin className="text-[#b02418] mt-1" size={20} />
                <div>
                  <p className="overline text-[#8d8a83] mb-1">{t("exhibition.location")}</p>
                  <p className="font-serif-display text-2xl md:text-3xl text-[#f5f2eb]">{t("exhibition.place")}</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="exhibition-dates">
                <Calendar className="text-[#b02418] mt-1" size={20} />
                <div>
                  <p className="overline text-[#8d8a83] mb-1">{t("exhibition.dates")}</p>
                  <p className="font-serif-display text-2xl md:text-3xl text-[#f5f2eb]">01. 08. — 31. 08. 2026</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-px bg-white/5">
            {pillars.map((p, i) => (
              <motion.div
                key={p}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="bg-[#0a0a0a] p-8 md:p-10 flex gap-5 items-start hover:bg-[#0d0d0d] transition-colors"
              >
                <span
                  className="mt-2 w-4 h-4 flex-shrink-0 rounded-sm"
                  style={{ background: dots[p], border: p === "ticha" ? "none" : "none" }}
                />
                <div>
                  <h3 className="overline text-lg text-[#f5f2eb] tracking-[0.2em]">
                    {t(`exhibition.pillars.${p}.t`)}
                  </h3>
                  <p className="mt-2 text-[#a3a09a] font-light leading-relaxed">
                    {t(`exhibition.pillars.${p}.d`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Exhibition;
